/* eslint-disable jsx-a11y/anchor-is-valid */
import React, { useContext, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "react-toastify";
import Breadcrumb from "../../components/Breadcrumb";
import { PatientAuthContext } from "../../contexts/PatientAuthContext";
import { patientApi } from "../../utils/patientApi";

const STEP_LOOKUP = "lookup";
const STEP_SELECT_PHONE = "select_phone";
const STEP_OTP = "otp";
const STEP_PASSWORD = "password";

const PatientLoginMain = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const redirectTo = searchParams.get("redirect") || "/patient-profile";
  const { login } = useContext(PatientAuthContext);

  const [step, setStep] = useState(STEP_LOOKUP);
  const [mode, setMode] = useState("otp"); // "otp" | "password"
  const [loading, setLoading] = useState(false);

  // Form fields
  const [identifier, setIdentifier] = useState(""); // MRNO or NIC
  const [identifierType, setIdentifierType] = useState("mrno"); // "mrno" | "nic"
  const [lookupResult, setLookupResult] = useState(null); // { mrno, firstName, lastName, phoneNumbers }
  const [selectedPhone, setSelectedPhone] = useState("");
  const [otp, setOtp] = useState("");
  const [password, setPassword] = useState("");
  const [otpInfo, setOtpInfo] = useState(null); // { maskedPhone, expiresInMinutes }

  // ── Step 1: Lookup ──────────────────────────────────────────────────────────
  const handleLookup = async (e) => {
    e.preventDefault();
    if (!identifier.trim()) return toast.error("Please enter your MRNO or NIC");
    setLoading(true);
    try {
      const body = identifierType === "mrno" ? { mrno: identifier.trim() } : { nic: identifier.trim() };
      const data = await patientApi.lookup(body);
      setLookupResult(data);
      if (mode === "password") {
        setStep(STEP_PASSWORD);
      } else {
        setStep(STEP_SELECT_PHONE);
      }
    } catch (err) {
      toast.error(err.message || "Patient not found");
    } finally {
      setLoading(false);
    }
  };

  // ── Step 2: Request OTP ─────────────────────────────────────────────────────
  const handleRequestOtp = async (phone) => {
    setSelectedPhone(phone);
    setLoading(true);
    try {
      const data = await patientApi.requestOtp({ mrno: lookupResult.mrno, selectedPhone: phone });
      setOtpInfo({ maskedPhone: data.maskedPhone, expiresInMinutes: data.expiresInMinutes });
      setStep(STEP_OTP);
      toast.success(`OTP sent to ${data.maskedPhone}`);
    } catch (err) {
      toast.error(err.message || "Failed to send OTP");
    } finally {
      setLoading(false);
    }
  };

  // ── Step 3: Verify OTP ──────────────────────────────────────────────────────
  const handleVerifyOtp = async (e) => {
    e.preventDefault();
    if (!otp.trim()) return toast.error("Please enter the OTP");
    setLoading(true);
    try {
      const data = await patientApi.verifyOtp({ mrno: lookupResult.mrno, otp: otp.trim() });
      login(data);
      toast.success(`Welcome, ${data.patient.firstName || data.patient.name || data.patient.mrno}!`);
      navigate(redirectTo);
    } catch (err) {
      toast.error(err.message || "Invalid OTP");
    } finally {
      setLoading(false);
    }
  };

  // ── Password login ──────────────────────────────────────────────────────────
  const handlePasswordLogin = async (e) => {
    e.preventDefault();
    if (!password.trim()) return toast.error("Please enter your password");
    setLoading(true);
    try {
      const data = await patientApi.signin({ mrno: lookupResult.mrno, password: password.trim() });
      login(data);
      toast.success(`Welcome back, ${data.patient.firstName || data.patient.name || data.patient.mrno}!`);
      navigate(redirectTo);
    } catch (err) {
      toast.error(err.message || "Login failed");
    } finally {
      setLoading(false);
    }
  };

  // ── Helpers ─────────────────────────────────────────────────────────────────
  const resetToLookup = () => {
    setStep(STEP_LOOKUP);
    setLookupResult(null);
    setSelectedPhone("");
    setOtp("");
    setPassword("");
    setOtpInfo(null);
  };

  const toggleMode = () => {
    setMode((m) => (m === "otp" ? "password" : "otp"));
    resetToLookup();
  };

  return (
    <main>
      <Breadcrumb title="Patient Login" />

      <div className="it-signup-area pt-120 pb-120">
        <div className="container">
          <div className="row justify-content-center">
            <div className="col-xl-6 col-lg-8 col-md-10">
              <div
                className="it-signup-wrap"
                style={{ background: "#fff", borderRadius: 12, padding: "40px 36px", boxShadow: "0 4px 24px rgba(0,0,0,0.08)" }}
              >
                {/* Mode toggle */}
                <div className="d-flex justify-content-between align-items-center mb-30">
                  <h4 className="it-signup-title mb-0">{mode === "otp" ? "Patient Login (OTP)" : "Patient Login (Password)"}</h4>
                  <button
                    onClick={toggleMode}
                    style={{
                      background: "none",
                      border: "1px solid #ddd",
                      borderRadius: 6,
                      padding: "4px 12px",
                      fontSize: "0.8rem",
                      cursor: "pointer",
                      color: "#555",
                    }}
                  >
                    {mode === "otp" ? "Use Password" : "Use OTP"}
                  </button>
                </div>

                {/* ── STEP: LOOKUP ── */}
                {step === STEP_LOOKUP && (
                  <form onSubmit={handleLookup}>
                    <div className="mb-20">
                      <div className="d-flex gap-3 mb-15">
                        <label style={{ cursor: "pointer", fontWeight: identifierType === "mrno" ? 600 : 400 }}>
                          <input
                            type="radio"
                            name="idType"
                            value="mrno"
                            checked={identifierType === "mrno"}
                            onChange={() => setIdentifierType("mrno")}
                            style={{ marginRight: 6 }}
                          />
                          MRNO
                        </label>
                        <label style={{ cursor: "pointer", fontWeight: identifierType === "nic" ? 600 : 400 }}>
                          <input
                            type="radio"
                            name="idType"
                            value="nic"
                            checked={identifierType === "nic"}
                            onChange={() => setIdentifierType("nic")}
                            style={{ marginRight: 6 }}
                          />
                          NIC / CNIC
                        </label>
                      </div>
                      <div className="it-signup-input">
                        <input
                          type="text"
                          placeholder={identifierType === "mrno" ? "e.g. M0100000132632" : "e.g. 35202-1234567-1"}
                          value={identifier}
                          onChange={(e) => setIdentifier(e.target.value)}
                          disabled={loading}
                        />
                      </div>
                    </div>
                    <button type="submit" className="it-btn-theme w-100" disabled={loading} style={{ padding: "12px 0", borderRadius: 8 }}>
                      {loading ? "Searching…" : "Continue"}
                    </button>
                  </form>
                )}

                {/* ── STEP: SELECT PHONE ── */}
                {step === STEP_SELECT_PHONE && lookupResult && (
                  <div>
                    <p className="mb-20" style={{ color: "#555" }}>
                      Hello,{" "}
                      <strong>
                        {lookupResult.firstName} {lookupResult.lastName}
                      </strong>
                      !<br />
                      Select a number to receive your OTP:
                    </p>
                    <div className="d-flex flex-column gap-3 mb-25">
                      {lookupResult.phoneNumbers.map((p, i) => (
                        <button
                          key={i}
                          onClick={() => handleRequestOtp(p.normalized)}
                          disabled={loading}
                          style={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                            padding: "14px 18px",
                            border: "1px solid #ddd",
                            borderRadius: 8,
                            background: "#f9f9f9",
                            cursor: "pointer",
                            fontSize: "1rem",
                            fontWeight: 500,
                            letterSpacing: 1,
                          }}
                        >
                          <span>
                            <i className="fa-regular fa-mobile me-2" style={{ color: "#888" }}></i>
                            {p.masked}
                          </span>
                          <span style={{ color: "#007bff", fontSize: "0.82rem" }}>{loading ? "Sending…" : "Send OTP →"}</span>
                        </button>
                      ))}
                    </div>
                    <button
                      onClick={resetToLookup}
                      style={{ background: "none", border: "none", color: "#888", cursor: "pointer", fontSize: "0.85rem" }}
                    >
                      ← Back
                    </button>
                  </div>
                )}

                {/* ── STEP: OTP ── */}
                {step === STEP_OTP && (
                  <form onSubmit={handleVerifyOtp}>
                    {otpInfo && (
                      <p className="mb-20" style={{ color: "#555" }}>
                        OTP sent to <strong>{otpInfo.maskedPhone}</strong>.<br />
                        <small style={{ color: "#e74c3c" }}>Expires in {otpInfo.expiresInMinutes} minutes.</small>
                      </p>
                    )}
                    <div className="it-signup-input mb-20">
                      <input
                        type="text"
                        inputMode="numeric"
                        maxLength={6}
                        placeholder="Enter 6-digit OTP"
                        value={otp}
                        onChange={(e) => setOtp(e.target.value.replace(/\D/g, ""))}
                        disabled={loading}
                        style={{ letterSpacing: "0.3rem", fontSize: "1.25rem", textAlign: "center" }}
                      />
                    </div>
                    <button
                      type="submit"
                      className="it-btn-theme w-100 mb-15"
                      disabled={loading}
                      style={{ padding: "12px 0", borderRadius: 8 }}
                    >
                      {loading ? "Verifying…" : "Verify OTP"}
                    </button>
                    <div className="d-flex justify-content-between">
                      <button
                        type="button"
                        onClick={() => setStep(STEP_SELECT_PHONE)}
                        style={{ background: "none", border: "none", color: "#888", cursor: "pointer", fontSize: "0.85rem" }}
                      >
                        ← Change number
                      </button>
                      <button
                        type="button"
                        onClick={() => handleRequestOtp(selectedPhone)}
                        disabled={loading}
                        style={{ background: "none", border: "none", color: "#007bff", cursor: "pointer", fontSize: "0.85rem" }}
                      >
                        Resend OTP
                      </button>
                    </div>
                  </form>
                )}

                {/* ── STEP: PASSWORD ── */}
                {step === STEP_PASSWORD && lookupResult && (
                  <form onSubmit={handlePasswordLogin}>
                    <p className="mb-20" style={{ color: "#555" }}>
                      Hello,{" "}
                      <strong>
                        {lookupResult.firstName} {lookupResult.lastName}
                      </strong>
                      !<br />
                      Enter your password to continue.
                    </p>
                    <div className="it-signup-input mb-20">
                      <input
                        type="password"
                        placeholder="Password"
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        disabled={loading}
                      />
                    </div>
                    <button
                      type="submit"
                      className="it-btn-theme w-100 mb-15"
                      disabled={loading}
                      style={{ padding: "12px 0", borderRadius: 8 }}
                    >
                      {loading ? "Logging in…" : "Login"}
                    </button>
                    <button
                      type="button"
                      onClick={resetToLookup}
                      style={{ background: "none", border: "none", color: "#888", cursor: "pointer", fontSize: "0.85rem" }}
                    >
                      ← Back
                    </button>
                  </form>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </main>
  );
};

export default PatientLoginMain;
